import { Stack } from "@mui/material";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  FormAutocomplete,
  SaveAndCancelButton,
} from "../../../../components/form";
import { updateUserProfile } from "../../../../api/profile";
import { useMutationWithDispatch } from "../../../../hooks/useMutationWithDispatch";
import { useAppSelector } from "../../../../store";
import {
  addOrUpdateInterestedSchema,
  AddOrUpdateInterestedFormData,
} from "./config";
import { AddOrUpdateIntrestedProps, UpdateProfileResponse } from "./types";
import {
  mapSelectedToFormValues,
  getTopicsPayload,
} from "./utils";

export default function AddOrUpdateIntrested({
  data,
  selectedIntrested,
  onSubmit,
  onCancel,
}: AddOrUpdateIntrestedProps) {
  const lists = useAppSelector((state) => state.lists);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<AddOrUpdateInterestedFormData>({
    resolver: zodResolver(addOrUpdateInterestedSchema),
    defaultValues: mapSelectedToFormValues(selectedIntrested),
  });

  const { mutateAsync: updateProfile, isPending: submitting } =
    useMutationWithDispatch<UpdateProfileResponse, { topics: any[] }>(
      (payload) => updateUserProfile(payload)
    );

  const topicOptions = useMemo(
    () => lists?.topics?.data || [],
    [lists?.topics?.data]
  );
  const priorityOptions = useMemo(
    () => lists?.intrestedInPriority?.data || [],
    [lists?.intrestedInPriority?.data]
  );

  const existingTopics = useMemo(
    () => (Array.isArray(data?.topics) ? data.topics : []),
    [data?.topics]
  );

  const onFormSubmit = async (formData: AddOrUpdateInterestedFormData) => {
    const payload = getTopicsPayload(
      formData,
      existingTopics,
      selectedIntrested
    );
    const res = await updateProfile(payload);
    if (res?.success) {
      onSubmit();
    }
  };

  return (
    <form onSubmit={handleSubmit(onFormSubmit)} noValidate>
      <Stack gap={2}>
        <FormAutocomplete
          name="label"
          control={control}
          label="Cybersecurity Products You're Interested In"
          required
          options={topicOptions}
          loading={lists?.topics?.loading}
          valueField="label"
          labelField="label"
          error={errors.label?.message}
        />

        <FormAutocomplete
          name="priority"
          control={control}
          label="Priority"
          required
          options={priorityOptions}
          valueField="label"
          labelField="label"
          error={errors.priority?.message}
        />

        <SaveAndCancelButton
          onCancelButtonClick={onCancel}
          loading={submitting}
        />
      </Stack>
    </form>
  );
}
